import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, UserCheck, ShieldCheck, Boxes, Loader2, ListChecks, Plane, TrendingUp, HandCoins, AlertTriangle, Landmark, BarChart3, FileWarning, Download, RotateCcw, Truck, CheckCircle2, Clock, Navigation } from "lucide-react";
import api, { money, openPdf } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AuxiliarPanel } from "./AuxiliarPanel";
import { CommercialIntelligence } from "../components/CommercialIntelligence";
import { CommandCenter } from "../components/CommandCenter";
import { toast } from "sonner";

const MONTHS_SHORT = ["", "Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const monthLabel = (p) => { const [y, m] = (p || "").split("-"); return `${MONTHS_SHORT[parseInt(m, 10)] || m} ${(y || "").slice(2)}`; };
const totalsStr = (t) => { const e = Object.entries(t || {}); return e.length ? e.map(([c, v]) => money(c, v)).join(" · ") : money("GTQ", 0); };

function Stat({ icon: Icon, label, value, testId, accent }) {
  return (
    <Card className="rounded-sm border shadow-none p-5 flex items-center gap-4" data-testid={testId}>
      <div className={`h-12 w-12 shrink-0 flex items-center justify-center rounded-sm ${accent || "bg-primary/10 text-primary"}`}>
        <Icon className="h-6 w-6" />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-heading font-black leading-none truncate">{value}</p>
        <p className="text-xs text-muted-foreground uppercase tracking-widest mt-1.5">{label}</p>
      </div>
    </Card>
  );
}

function KPI({ icon: Icon, label, value, sub, subDanger, testId }) {
  return (
    <Card className="rounded-sm border shadow-none p-5 space-y-2" data-testid={testId}>
      <div className="flex items-center gap-2 text-muted-foreground"><Icon className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-widest">{label}</span></div>
      <p className="text-2xl font-heading font-black leading-none">{value}</p>
      {sub != null && <p className={`text-xs ${subDanger ? "text-red-600 font-semibold" : "text-muted-foreground"}`}>{sub}</p>}
    </Card>
  );
}

const wazeLink = (a) => `https://waze.com/ul?q=${encodeURIComponent(a || "")}&navigate=yes`;

function MessengerPanel() {
  const [data, setData] = useState(null);
  const [starting, setStarting] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { api.get("/routes/today").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const c = data.counts || { total: 0, done: 0, pending: 0, problem: 0 };
  const nt = data.next_task;
  const startRoute = async () => {
    if (nt && nt.status === "Pendiente") {
      setStarting(true);
      try { await api.patch(`/route-tasks/${nt.id}/status`, { status: "En camino" }); toast.success("Ruta iniciada · primera tarea En camino"); }
      catch (e) { toast.error("No se pudo iniciar la ruta"); } finally { setStarting(false); }
    }
    navigate("/rutas");
  };
  return (
    <section className="space-y-4" data-testid="messenger-panel">
      <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Truck className="h-5 w-5 text-primary" /> Mi ruta de hoy</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={ListChecks} label="Tareas hoy" value={c.total} testId="msg-total" accent="bg-muted text-foreground" />
        <Stat icon={CheckCircle2} label="Realizadas" value={c.done} testId="msg-done" accent="bg-green-100 text-green-700" />
        <Stat icon={Clock} label="Pendientes" value={c.pending} testId="msg-pending" accent="bg-amber-100 text-amber-700" />
        <Stat icon={AlertTriangle} label="Con problema" value={c.problem} testId="msg-problem" accent="bg-red-100 text-red-700" />
      </div>
      {nt && (
        <Card className="rounded-sm border-l-4 border-l-primary shadow-none p-5" data-testid="msg-next-task">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-1">Próxima tarea</p>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div><p className="font-heading font-bold">#{nt.order} · {nt.place}</p>
              <p className="text-sm text-muted-foreground">{nt.address || "Sin dirección"}</p></div>
            <div className="flex items-center gap-2">
              {nt.address && <a href={wazeLink(nt.address)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="rounded-sm gap-1.5" data-testid="msg-next-waze"><Navigation className="h-4 w-4" /> Waze</Button></a>}
              <Button size="sm" className="rounded-sm gap-1.5" onClick={startRoute} disabled={starting} data-testid="msg-start-route"><Truck className="h-4 w-4" /> {nt.status === "Pendiente" ? "Iniciar ruta" : "Continuar ruta"}</Button>
            </div>
          </div>
        </Card>
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">{data.routes.length > 0 ? `${data.routes.length} ruta(s) asignada(s) hoy` : "No tienes rutas asignadas hoy."}</p>
        <Button variant="outline" size="sm" className="rounded-sm gap-1.5" onClick={() => navigate("/rutas")} data-testid="msg-go-routes"><Truck className="h-4 w-4" /> Ver mis rutas</Button>
      </div>
    </section>
  );
}

export default function Dashboard() {
  const { user, can } = useAuth();
  const isAux = user?.role_name === "Auxiliar Administrativo y Financiero";
  const isDirector = user?.is_admin || user?.role_name === "Dirección General";
  const isMessenger = user?.role_name === "Mensajero";
  const [stats, setStats] = useState(null);
  const [fin, setFin] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [vendorId, setVendorId] = useState("");
  const navigate = useNavigate();

  const loadFinance = (s = start, e = end, v = vendorId) => {
    const p = new URLSearchParams();
    if (s && e) { p.set("start", s); p.set("end", e); }
    if (v && v !== "all") p.set("vendor_id", v);
    const qs = p.toString();
    api.get(`/dashboard/finance${qs ? `?${qs}` : ""}`).then((r) => setFin(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    loadFinance();
  }, []);

  const applyFilters = () => {
    if ((start && !end) || (!start && end)) return toast.error("Selecciona fecha inicial y final");
    loadFinance();
  };
  const clearFilters = () => { setStart(""); setEnd(""); setVendorId(""); loadFinance("", "", ""); };
  const exportPdf = async () => {
    const p = new URLSearchParams();
    if (start && end) { p.set("start", start); p.set("end", end); }
    if (vendorId && vendorId !== "all") p.set("vendor_id", vendorId);
    const qs = p.toString();
    try { await openPdf(`/dashboard/finance/pdf${qs ? `?${qs}` : ""}`); } catch { toast.error("No se pudo generar el PDF"); }
  };

  if (!stats) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const maxRole = Math.max(1, ...stats.by_role.map((r) => r.count));
  const showFinance = fin && (fin.can.invoices || fin.can.receipts || fin.can.treasury);
  const rangeActive = fin?.filter?.start && fin?.filter?.end;
  const per = rangeActive ? "del periodo" : "del mes";
  const chart = fin?.sales_by_month || [];
  const maxSale = Math.max(1, ...chart.map((c) => c.total_gtq));

  return (
    <div className="space-y-8">
      <CommandCenter />

      {isAux && <AuxiliarPanel />}

      {isMessenger && <MessengerPanel />}

      {can("invoices:view") && !isAux && !isMessenger && <CommercialIntelligence />}

      {showFinance && !isAux && (
        <section className="space-y-4" data-testid="finance-dashboard">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <h3 className="font-heading font-bold text-lg flex items-center gap-2"><BarChart3 className="h-5 w-5 text-primary" /> Resumen financiero {rangeActive ? "por periodo" : "del mes"}</h3>
            <div className="flex flex-wrap items-center gap-2">
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-sm h-9 w-40 text-xs" data-testid="filter-start" />
              <span className="text-xs text-muted-foreground">a</span>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-sm h-9 w-40 text-xs" data-testid="filter-end" />
              {fin.can.all && (
                <Select value={vendorId || "all"} onValueChange={setVendorId}>
                  <SelectTrigger className="rounded-sm h-9 w-44 text-xs" data-testid="filter-vendor"><SelectValue placeholder="Vendedor" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">Todos los vendedores</SelectItem>{(fin.vendors || []).map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <Button size="sm" className="rounded-sm h-9 text-xs font-bold" onClick={applyFilters} data-testid="apply-filters-btn">Aplicar</Button>
              {(rangeActive || (fin.filter?.vendor_id)) && <Button size="sm" variant="ghost" className="rounded-sm h-9 text-xs" onClick={clearFilters} data-testid="clear-filters-btn"><RotateCcw className="h-3.5 w-3.5" /></Button>}
              <Button size="sm" variant="outline" className="rounded-sm h-9 text-xs font-bold gap-1.5" onClick={exportPdf} data-testid="export-pdf-btn"><Download className="h-3.5 w-3.5" /> PDF</Button>
            </div>
          </div>
          {rangeActive && <p className="text-xs text-muted-foreground">Mostrando {fin.filter.start} a {fin.filter.end}{fin.filter.vendor_name ? ` · ${fin.filter.vendor_name}` : ""}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {fin.can.invoices && <KPI icon={TrendingUp} label={`Ventas ${per}`} value={totalsStr(fin.sales_month)} sub="Facturación emitida" testId="kpi-sales-month" />}
            {fin.can.receipts && <KPI icon={HandCoins} label={`Cobros ${per}`} value={totalsStr(fin.collected_month)} sub="Recibos de caja" testId="kpi-collected-month" />}
            {fin.can.invoices && <KPI icon={FileWarning} label="Cuentas por cobrar" value={totalsStr(fin.receivables)} sub={`${fin.open_invoices} facturas abiertas`} testId="kpi-receivables" />}
            {fin.can.treasury && <KPI icon={Landmark} label="Saldos bancarios" value={totalsStr(fin.bank_totals)} sub={`${fin.bank_balances.length} cuentas`} testId="kpi-bank" />}
          </div>

          {fin.can.invoices && Object.keys(fin.receivables_overdue || {}).length > 0 && (
            <Card className="rounded-sm border-l-4 border-l-red-500 shadow-none p-3 flex items-center gap-2 text-sm bg-red-500/10" data-testid="kpi-overdue">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="text-red-700">Saldo vencido: <strong>{totalsStr(fin.receivables_overdue)}</strong></span>
            </Card>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {fin.can.invoices && (
              <Card className="rounded-sm border shadow-none p-5" data-testid="sales-chart">
                <h4 className="font-heading font-bold mb-4 text-sm uppercase tracking-widest text-muted-foreground">Ventas últimos 6 meses (GTQ)</h4>
                <div className="flex items-end justify-between gap-2 h-40">
                  {chart.map((c) => (
                    <div key={c.period} className="flex-1 flex flex-col items-center justify-end gap-1.5 h-full">
                      <span className="text-[10px] font-mono font-bold text-foreground">{c.total_gtq >= 1000 ? `${(c.total_gtq / 1000).toFixed(1)}k` : c.total_gtq}</span>
                      <div className="w-full bg-primary rounded-t-sm transition-all hover:bg-primary/80" style={{ height: `${Math.max(3, (c.total_gtq / maxSale) * 100)}%` }} title={money("GTQ", c.total_gtq)} />
                      <span className="text-[10px] text-muted-foreground uppercase">{monthLabel(c.period)}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {fin.can.invoices && (
              <Card className="rounded-sm border shadow-none p-5" data-testid="top-debtors">
                <h4 className="font-heading font-bold mb-4 text-sm uppercase tracking-widest text-muted-foreground">Top clientes deudores</h4>
                {fin.top_debtors.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Sin saldos pendientes 🎉</p> : (
                  <div className="space-y-2.5">
                    {fin.top_debtors.map((d, i) => (
                      <div key={d.client_id} className="flex items-center gap-3" data-testid={`debtor-row-${i}`}>
                        <span className="h-6 w-6 shrink-0 flex items-center justify-center bg-muted rounded-sm text-xs font-bold font-mono">{i + 1}</span>
                        <span className="flex-1 text-sm truncate">{d.client_name}</span>
                        <span className="text-sm font-mono font-bold text-primary">{money(d.currency, d.balance)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}
          </div>
        </section>
      )}

      <section className="space-y-4">
        <h3 className="font-heading font-bold text-lg">Mi actividad</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Stat icon={ListChecks} label="Mis tareas abiertas" value={stats.my_open_tasks ?? 0} testId="stat-my-tasks" />
          <Stat icon={Plane} label="Solicitudes pendientes" value={stats.my_pending_vacations ?? 0} testId="stat-my-vacations" />
          {isDirector && <Stat icon={Boxes} label="Módulos del sistema" value={stats.modules} testId="stat-modules" />}
        </div>
      </section>

      {stats.by_role && stats.total_users != null && isDirector && (
        <section className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Stat icon={Users} label="Usuarios totales" value={stats.total_users} testId="stat-total-users" accent="bg-muted text-foreground" />
            <Stat icon={UserCheck} label="Usuarios activos" value={stats.active_users} testId="stat-active-users" accent="bg-muted text-foreground" />
            <Stat icon={ShieldCheck} label="Roles definidos" value={stats.total_roles} testId="stat-total-roles" accent="bg-muted text-foreground" />
          </div>

          {isDirector && (
          <Card className="rounded-sm border shadow-none p-5">
            <h3 className="font-heading font-bold text-lg mb-4">Distribución de personal por rol</h3>
            <div className="space-y-3">
              {stats.by_role.map((r) => (
                <div key={r.name} className="flex items-center gap-3" data-testid="role-dist-row">
                  <span className="w-48 shrink-0 text-sm truncate">{r.name}</span>
                  <div className="flex-1 h-6 bg-muted rounded-sm overflow-hidden">
                    <div className="h-full bg-primary flex items-center justify-end pr-2" style={{ width: `${(r.count / maxRole) * 100}%` }}>
                      {r.count > 0 && <span className="text-[11px] font-bold font-mono text-primary-foreground">{r.count}</span>}
                    </div>
                  </div>
                  {r.count === 0 && <span className="text-[11px] font-mono text-muted-foreground w-4">0</span>}
                </div>
              ))}
            </div>
          </Card>
          )}
        </section>
      )}
    </div>
  );
}
